import { TrialCheckoutGate } from './components/auth/TrialCheckoutGate';
import { useEffect, lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { AppShell } from './components/layout/AppShell';
import { AuthGuard } from './components/auth/AuthGuard';
import { AdminGuard } from './components/admin/AdminGuard';
import { AdminLayout } from './components/admin/AdminLayout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PageLoader } from './components/ui/PageLoader';
import { migratePhotosToIndexedDB } from './utils/photo-migration';
import { migrateArtworksToIndexedDB } from './utils/art-migration';
import { captureUtmParams } from './utils/utm';
import { startAnalytics, track } from './lib/analytics';
import { supabase } from './lib/supabase';
import { ensureSkillGraphActivated } from './data/skill-graph-init';
import { backfillLanguageCompletions } from './store/languageStore';
import { backfillProjectCompletions } from './store/projectStore';
import { useChildStore } from './store';
import { useSettingsStore } from './store/settingsStore';
import { useAuthStore } from './store/authStore';
import { useLiveRegion } from './hooks/useLiveRegion';
import { useShowRewards } from './hooks/useShowRewards';
import { BetaRouteGuard } from './components/beta/BetaRouteGuard';
import { isBetaFeatureEnabled, type BetaHeldFeature } from './config/beta-surface';

// Lazy-load all page components for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage').then(m => ({ default: m.LandingPage })));
const ChristianHomeschoolPage = lazy(() => import('./pages/ChristianHomeschoolPage').then(m => ({ default: m.ChristianHomeschoolPage })));
const HomeschoolGamesPage = lazy(() => import('./pages/HomeschoolGamesPage').then(m => ({ default: m.HomeschoolGamesPage })));
const CoolJobsPage = lazy(() => import('./pages/CoolJobsPage').then(m => ({ default: m.CoolJobsPage })));
const VirtualFieldTripsPage = lazy(() => import('./pages/VirtualFieldTripsPage').then(m => ({ default: m.VirtualFieldTripsPage })));
const LanguageLearningPage = lazy(() => import('./pages/LanguageLearningPage').then(m => ({ default: m.LanguageLearningPage })));
const MusicArtPage = lazy(() => import('./pages/MusicArtPage').then(m => ({ default: m.MusicArtPage })));
const HomeschoolCurriculumPage = lazy(() => import('./pages/HomeschoolCurriculumPage').then(m => ({ default: m.HomeschoolCurriculumPage })));
const PuttDayDevPage = lazy(() => import('./pages/PuttDayDevPage').then(m => ({ default: m.PuttDayDevPage })));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const SignUpPage = lazy(() => import('./pages/auth/SignUpPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const WelcomePage = lazy(() => import('./pages/WelcomePage').then(m => ({ default: m.WelcomePage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const ActivityPage = lazy(() => import('./pages/ActivityPage').then(m => ({ default: m.ActivityPage })));
const SkillsPage = lazy(() => import('./pages/SkillsPage').then(m => ({ default: m.SkillsPage })));
const ShopPage = lazy(() => import('./pages/ShopPage').then(m => ({ default: m.ShopPage })));
const ProgressPage = lazy(() => import('./pages/ProgressPage').then(m => ({ default: m.ProgressPage })));
const LibraryPage = lazy(() => import('./pages/LibraryPage').then(m => ({ default: m.LibraryPage })));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage').then(m => ({ default: m.PortfolioPage })));
const ReportsPage = lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const ParentPage = lazy(() => import('./pages/ParentPage').then(m => ({ default: m.ParentPage })));
const ScavengerHuntPage = lazy(() => import('./pages/ScavengerHuntPage').then(m => ({ default: m.ScavengerHuntPage })));
const FieldTripsPage = lazy(() => import('./pages/FieldTripsPage').then(m => ({ default: m.FieldTripsPage })));
const ParkDetailPage = lazy(() => import('./pages/ParkDetailPage').then(m => ({ default: m.ParkDetailPage })));
const MusicStudioPage = lazy(() => import('./pages/MusicStudioPage').then(m => ({ default: m.MusicStudioPage })));
const GamesPage = lazy(() => import('./pages/GamesPage').then(m => ({ default: m.GamesPage })));
const MapExplorerPage = lazy(() => import('./pages/MapExplorerPage').then(m => ({ default: m.MapExplorerPage })));
const ArtStudioPage = lazy(() => import('./pages/ArtStudioPage').then(m => ({ default: m.ArtStudioPage })));
const CoursesPage = lazy(() => import('./pages/CoursesPage').then(m => ({ default: m.CoursesPage })));
const NatureIdPage = lazy(() => import('./pages/NatureIdPage').then(m => ({ default: m.NatureIdPage })));
const CookingPage = lazy(() => import('./pages/CookingPage').then(m => ({ default: m.CookingPage })));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AmbassadorPage = lazy(() => import('./pages/AmbassadorPage').then(m => ({ default: m.AmbassadorPage })));
const InvitesPage = lazy(() => import('./pages/InvitesPage'));
const MissionsPage = lazy(() => import('./pages/MissionsPage').then(m => ({ default: m.MissionsPage })));
const MissionDetailPage = lazy(() => import('./pages/MissionDetailPage').then(m => ({ default: m.MissionDetailPage })));
const StatePuzzlePage = lazy(() => import('./pages/StatePuzzlePage').then(m => ({ default: m.StatePuzzlePage })));
const JourneyPage = lazy(() => import('./pages/JourneyPage').then(m => ({ default: m.JourneyPage })));
const CountryCoursePage = lazy(() => import('./pages/CountryCoursePage').then(m => ({ default: m.CountryCoursePage })));
const ExpeditionPage = lazy(() => import('./pages/ExpeditionPage').then(m => ({ default: m.ExpeditionPage })));
const PrintablesPage = lazy(() => import('./pages/PrintablesPage').then(m => ({ default: m.PrintablesPage })));
const WeeklyDigestPage = lazy(() => import('./pages/WeeklyDigestPage').then(m => ({ default: m.WeeklyDigestPage })));
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage = lazy(() => import('./pages/legal/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })));
const CoppaNoticePage = lazy(() => import('./pages/legal/CoppaNoticePage').then(m => ({ default: m.CoppaNoticePage })));
const StatusPage = lazy(() => import('./pages/StatusPage').then(m => ({ default: m.StatusPage })));
const PlayHubPage = lazy(() => import('./pages/PlayHubPage').then(m => ({ default: m.PlayHubPage })));
const ExploreHubPage = lazy(() => import('./pages/ExploreHubPage').then(m => ({ default: m.ExploreHubPage })));
const CreateHubPage = lazy(() => import('./pages/CreateHubPage').then(m => ({ default: m.CreateHubPage })));
const MeHubPage = lazy(() => import('./pages/MeHubPage').then(m => ({ default: m.MeHubPage })));
const LearnHubPage = lazy(() => import('./pages/LearnHubPage').then(m => ({ default: m.LearnHubPage })));
const JobsPage = lazy(() => import('./pages/JobsPage').then(m => ({ default: m.JobsPage })));
const SubjectDetailPage = lazy(() => import('./pages/SubjectDetailPage').then(m => ({ default: m.SubjectDetailPage })));
const LanguagesPage = lazy(() => import('./pages/LanguagesPage').then(m => ({ default: m.LanguagesPage })));
const StoriesPage = lazy(() => import('./pages/StoriesPage').then(m => ({ default: m.StoriesPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const SkillMapPage = lazy(() => import('./pages/SkillMapPage').then(m => ({ default: m.SkillMapPage })));
const CompetitionsPage = lazy(() => import('./pages/CompetitionsPage').then(m => ({ default: m.CompetitionsPage })));
const DesignEditorPage = lazy(() => import('./pages/DesignEditorPage').then(m => ({ default: m.DesignEditorPage })));
const ActivityPrint = lazy(() => import('./routes/print/ActivityPrint'));
const WeekPrint = lazy(() => import('./routes/print/WeekPrint'));
const ProjectPrint = lazy(() => import('./routes/print/ProjectPrint'));

/** Wraps each route in its own Suspense + ErrorBoundary so one page crash does not kill the whole app. */
function SafePage({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

function AppRoutes() {
  const { children } = useChildStore();
  const user = useAuthStore(s => s.user);
  const initialized = useAuthStore(s => s.initialized);
  const hasChildren = children.length > 0;
  const showRewards = useShowRewards();

  // Wait for the async Supabase session check to finish before routing.
  // Without this, a logged-in user sees LandingPage briefly on every refresh
  // because auth starts as null and the route is committed before initialize() resolves.
  if (!initialized) return <PageLoader />;

  // Closed-beta gating: every gated route lives inside `<AppShell>` which is
  // wrapped in `<AuthGuard>` below. The root `/` element is the only piece
  // that has to hand-roll the user → dashboard / welcome / landing fork —
  // everything else inherits the guard automatically.
  //
  // Authenticated user + has a child profile → Dashboard
  // Authenticated user, no child profile yet → Welcome (onboarding)
  // Unauthenticated → Landing (public marketing page; CTA → /login)
  const rootElement = user
    ? <TrialCheckoutGate>{hasChildren ? <DashboardPage /> : <WelcomePage />}</TrialCheckoutGate>
    : <LandingPage />;
  const childPage = (page: ReactNode) =>
    hasChildren ? <SafePage>{page}</SafePage> : <Navigate to="/" />;
  const gatedChildPage = (feature: BetaHeldFeature, page: ReactNode) =>
    hasChildren ? <SafePage><BetaRouteGuard feature={feature}>{page}</BetaRouteGuard></SafePage> : <Navigate to="/" />;
  const gatedPage = (feature: BetaHeldFeature, page: ReactNode) => (
    <SafePage><BetaRouteGuard feature={feature}>{page}</BetaRouteGuard></SafePage>
  );

  return (
    <Routes>
      {/* Auth routes (no AppShell) */}
      <Route path="/login" element={<SafePage><LoginPage /></SafePage>} />
      <Route path="/signup" element={<SafePage><SignUpPage /></SafePage>} />
      <Route path="/forgot-password" element={<SafePage><ForgotPasswordPage /></SafePage>} />
      <Route path="/reset-password" element={<SafePage><ResetPasswordPage /></SafePage>} />
      <Route path="/verify-email" element={<SafePage><VerifyEmailPage /></SafePage>} />
      <Route path="/pricing" element={gatedPage('pricing', <PricingPage />)} />
      {/* Ad landing page for christian-homeschool search traffic — public,
          standalone (no AppShell), same shell as the root LandingPage. */}
      <Route path="/christian-homeschool" element={<SafePage><ChristianHomeschoolPage /></SafePage>} />
      {/* Ad-angle landers — public, standalone (no AppShell), on the shared
          landing kit. Slugs are distinct from the authed in-app routes
          (/games, /jobs, /field-trips) and double as SEO landing pages. */}
      <Route path="/homeschool-games" element={<SafePage><HomeschoolGamesPage /></SafePage>} />
      <Route path="/cool-jobs-for-kids" element={<SafePage><CoolJobsPage /></SafePage>} />
      <Route path="/virtual-field-trips" element={<SafePage><VirtualFieldTripsPage /></SafePage>} />
      <Route path="/language-learning-for-kids" element={<SafePage><LanguageLearningPage /></SafePage>} />
      <Route path="/music-and-art-for-kids" element={<SafePage><MusicArtPage /></SafePage>} />
      <Route path="/homeschool-curriculum" element={<SafePage><HomeschoolCurriculumPage /></SafePage>} />

      {/* Print routes (no AppShell — clean page for printing) */}
      <Route path="/print/activity/:activityId" element={<SafePage><AuthGuard requireEmailVerified><ActivityPrint /></AuthGuard></SafePage>} />
      <Route path="/print/week/:weekIso" element={<SafePage><AuthGuard requireEmailVerified><WeekPrint /></AuthGuard></SafePage>} />
      <Route path="/print/project/:projectId" element={<SafePage><AuthGuard requireEmailVerified><ProjectPrint /></AuthGuard></SafePage>} />

      {/* Admin route (no AppShell, own layout) */}
      <Route path="/admin" element={
        <SafePage>
          <AdminGuard>
            <AdminLayout>
              <AdminPage />
            </AdminLayout>
          </AdminGuard>
        </SafePage>
      } />

      {/* Legal pages — public so users can read before signing up.
          Mounted with AppShell but OUTSIDE the AuthGuard so the marketing
          page nav still works for unauthed visitors. */}
      <Route element={<AppShell />}>
        <Route path="/privacy" element={<SafePage><PrivacyPolicyPage /></SafePage>} />
        <Route path="/terms" element={<SafePage><TermsOfServicePage /></SafePage>} />
        <Route path="/coppa" element={<SafePage><CoppaNoticePage /></SafePage>} />
        <Route path="/status" element={<SafePage><StatusPage /></SafePage>} />
        {import.meta.env.DEV && (
          <Route path="/putt-test" element={<SafePage><PuttDayDevPage /></SafePage>} />
        )}
        {/* Root: dashboard / welcome / landing fork. The AuthGuard would
            kick unauthed users to /login, but `/` is the marketing landing
            page for them — so it stays outside the guard. */}
        <Route path="/" element={<SafePage>{rootElement}</SafePage>} />
      </Route>

      {/* Closed-beta gated app routes (AuthGuard + AppShell).
          AuthGuard requires a Supabase user with a verified email; otherwise
          it redirects to /login or /verify-email. The `hasChildren` checks
          inside each route are now ONLY for "do you have a child profile
          yet?" — Supabase auth is enforced one level up. */}
      <Route element={<AuthGuard requireEmailVerified><AppShell /></AuthGuard>}>
        <Route path="/welcome" element={<SafePage><WelcomePage /></SafePage>} />
        <Route path="/ambassador" element={gatedPage('ambassador', <AmbassadorPage />)} />
        <Route path="/invites" element={gatedPage('invite-flows', <InvitesPage />)} />
        <Route path="/activity/:activityId" element={<SafePage><ActivityPage /></SafePage>} />
        <Route path="/projects" element={childPage(<ProjectsPage />)} />
        {/* /badges deprecated 2026-05 — Skills page is the canonical view. */}
        <Route path="/badges" element={<Navigate to="/skills" replace />} />
        <Route path="/skills" element={childPage(<SkillsPage />)} />
        <Route path="/shop" element={hasChildren && showRewards && isBetaFeatureEnabled('shop') ? childPage(<ShopPage />) : <Navigate to="/me" replace />} />
        <Route path="/progress" element={childPage(<ProgressPage />)} />
        <Route path="/library" element={childPage(<LibraryPage />)} />
        <Route path="/portfolio" element={childPage(<PortfolioPage />)} />
        <Route path="/reports" element={gatedChildPage('reports', <ReportsPage />)} />
        <Route path="/explore" element={childPage(<ExploreHubPage />)} />
        <Route path="/explore/:huntId" element={childPage(<ScavengerHuntPage />)} />
        <Route path="/field-trips" element={childPage(<FieldTripsPage />)} />
        <Route path="/jobs" element={gatedChildPage('jobs', <JobsPage />)} />
        <Route path="/field-trips/:parkId" element={childPage(<ParkDetailPage />)} />
        <Route path="/music" element={childPage(<MusicStudioPage />)} />
        <Route path="/games" element={childPage(<GamesPage />)} />
        <Route path="/maps" element={childPage(<MapExplorerPage />)} />
        <Route path="/art" element={childPage(<ArtStudioPage />)} />
        <Route path="/design" element={childPage(<DesignEditorPage />)} />
        <Route path="/design/:challengeId" element={childPage(<DesignEditorPage />)} />
        <Route path="/courses" element={childPage(<CoursesPage />)} />
        <Route path="/nature-id" element={gatedChildPage('nature-id', <NatureIdPage />)} />
        <Route path="/cooking" element={childPage(<CookingPage />)} />
        <Route path="/settings" element={childPage(<ParentPage />)} />
        <Route path="/missions" element={gatedChildPage('missions', <MissionsPage />)} />
        <Route path="/missions/:missionId" element={gatedChildPage('missions', <MissionDetailPage />)} />
        <Route path="/maps/puzzle" element={gatedChildPage('map-puzzle', <StatePuzzlePage />)} />
        <Route path="/maps/journey/:id" element={gatedChildPage('map-journeys', <JourneyPage />)} />
        <Route path="/maps/courses" element={gatedChildPage('map-courses', <CountryCoursePage />)} />
        <Route path="/maps/course/:code" element={gatedChildPage('map-courses', <CountryCoursePage />)} />
        <Route path="/maps/expeditions" element={gatedChildPage('map-expeditions', <ExpeditionPage />)} />
        <Route path="/maps/expedition/:id" element={gatedChildPage('map-expeditions', <ExpeditionPage />)} />
        <Route path="/maps/printables" element={gatedChildPage('map-printables', <PrintablesPage />)} />
        <Route path="/digest" element={gatedChildPage('weekly-digest', <WeeklyDigestPage />)} />
        {/* Hub routes */}
        <Route path="/play" element={childPage(<PlayHubPage />)} />
        <Route path="/create" element={childPage(<CreateHubPage />)} />
        <Route path="/me" element={childPage(<MeHubPage />)} />
        <Route path="/learn" element={childPage(<LearnHubPage />)} />
        <Route path="/learn/subjects/:subject" element={childPage(<SubjectDetailPage />)} />
        <Route path="/learn/languages" element={childPage(<LanguagesPage />)} />
        <Route path="/learn/languages/:code" element={childPage(<LanguagesPage />)} />
        <Route path="/stories" element={gatedChildPage('stories', <StoriesPage />)} />
        <Route path="/skills/map" element={gatedChildPage('skill-map', <SkillMapPage />)} />
        <Route path="/competitions" element={gatedChildPage('competitions', <CompetitionsPage />)} />
        <Route path="*" element={<SafePage><NotFoundPage /></SafePage>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  const darkMode = useSettingsStore(s => s.darkMode);
  // Mount aria-live singleton once at app root so XP/coin/level-up
  // announcements work from any descendant (addresses P7 a11y finding).
  useLiveRegion();

  useEffect(() => {
    migratePhotosToIndexedDB().catch(() => {});
    migrateArtworksToIndexedDB().catch(() => {});
    backfillLanguageCompletions();
    backfillProjectCompletions();
    // First-touch ad attribution — ad clicks land with utm_* params on the
    // marketing page; signup happens screens later, so stash them now.
    captureUtmParams();

    // Product-funnel analytics. app_open (for authed users) is the retention
    // signal behind the activation metric; checkout_success closes the loop
    // when Stripe redirects back with ?checkout=success.
    startAnalytics();
    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user) track('app_open');
    });
    if (new URLSearchParams(window.location.search).get('checkout') === 'success') {
      track('checkout_success');
      // Stripe redirects quickly; refresh once immediately and once after the
      // webhook has had time to reconcile so paid access and pricing CTAs do
      // not remain stale after a successful checkout.
      const refreshSubscription = useAuthStore.getState().refreshSubscription;
      void refreshSubscription();
      window.setTimeout(() => { void refreshSubscription(); }, 2000);
    }

    // Activate the skill-graph DAG once, when the browser is idle — populates
    // activityToSkills (lazy ~3.8 MB chunk + ~60 ms build) so prerequisite-aware
    // sequencing / skill paths work. Nothing reads it during first paint.
    const idle: (cb: () => void) => void =
      typeof window.requestIdleCallback === 'function'
        ? (cb) => window.requestIdleCallback(cb, { timeout: 4000 })
        : (cb) => window.setTimeout(cb, 1200);
    idle(() => { void ensureSkillGraphActivated(); });
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  return (
    <ErrorBoundary>
      <MotionConfig reducedMotion="user">
        <BrowserRouter>
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-bold"
          >
            Skip to content
          </a>
          <AppRoutes />
        </BrowserRouter>
      </MotionConfig>
    </ErrorBoundary>
  );
}
